import CloseIcon from '@mui/icons-material/Close'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import NotificationsIcon from '@mui/icons-material/Notifications'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import { Badge, Box, Divider, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslate } from 'react-admin'

import AnnouncementDetail from './AnnouncementDetail'
import AnnouncementRow from './AnnouncementRow'
import { useAnnouncements } from '../../domain/announcements'
import type { Announcement } from '../../types/announcement'

interface OpenFlaggedAnnouncementOnceParams {
  announcements: Announcement[]
  isLoaded: boolean
  openDetail: (id: string) => void
}

const useOpenFlaggedAnnouncementOnce = ({ announcements, isLoaded, openDetail }: OpenFlaggedAnnouncementOnceParams) => {
  const hasChecked = useRef(false)

  useEffect(() => {
    if (!isLoaded || hasChecked.current) {
      return
    }
    hasChecked.current = true
    const flagged = announcements.find((announcement) => announcement.opens_dialog)
    if (flagged) {
      openDetail(flagged.id)
    }
  }, [announcements, isLoaded, openDetail])
}

interface BellButtonProps {
  unreadCount: number
  isDrawerOpen: boolean
  onClick: () => void
}

const BellButton = ({ unreadCount, isDrawerOpen, onClick }: BellButtonProps) => {
  const translate = useTranslate()
  const title = translate('mymove.whatsNew.title')

  return (
    <Tooltip title={title}>
      <IconButton
        color="inherit"
        onClick={onClick}
        aria-label={unreadCount > 0 ? translate('mymove.whatsNew.unread', { smart_count: unreadCount }) : title}
      >
        <Badge badgeContent={unreadCount} color="error" invisible={unreadCount === 0}>
          {unreadCount > 0 || isDrawerOpen ? <NotificationsIcon /> : <NotificationsNoneIcon />}
        </Badge>
      </IconButton>
    </Tooltip>
  )
}

const WhatsNewBell = () => {
  const translate = useTranslate()
  const { announcements, isLoaded, unreadCount, markSeen } = useAnnouncements()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)

  const openDetail = useCallback(
    (id: string) => {
      setOpenId(id)
      markSeen([id])
    },
    [markSeen],
  )
  const follow = (id: string) => {
    markSeen([id])
    setOpenId(null)
    setIsDrawerOpen(false)
  }
  const markAllSeen = () =>
    markSeen(announcements.filter((announcement) => announcement.unread).map((announcement) => announcement.id))
  useOpenFlaggedAnnouncementOnce({ announcements, isLoaded, openDetail })

  return (
    <>
      <BellButton unreadCount={unreadCount} isDrawerOpen={isDrawerOpen} onClick={() => setIsDrawerOpen(true)} />
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 380 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
          <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
            {translate('mymove.whatsNew.title')}
          </Typography>
          {unreadCount > 0 && (
            <Tooltip title={translate('mymove.whatsNew.markAllAsRead')}>
              <IconButton onClick={markAllSeen} aria-label={translate('mymove.whatsNew.markAllAsRead')}>
                <DoneAllIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={() => setIsDrawerOpen(false)} aria-label={translate('mymove.whatsNew.close')}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        {announcements.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            {translate('mymove.whatsNew.empty')}
          </Typography>
        ) : (
          <Stack divider={<Divider />}>
            {announcements.map((announcement) => (
              <AnnouncementRow
                key={announcement.id}
                announcement={announcement}
                onOpen={() => openDetail(announcement.id)}
                onFollow={() => follow(announcement.id)}
              />
            ))}
          </Stack>
        )}
      </Drawer>
      <AnnouncementDetail
        announcement={announcements.find((announcement) => announcement.id === openId)}
        onClose={() => setOpenId(null)}
        onFollow={follow}
      />
    </>
  )
}

export default WhatsNewBell
